import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../lib/apiBase';

const WorkspaceContext = createContext(null);

export const useWorkspace = () => {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return ctx;
};

export const WorkspaceProvider = ({ children }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState(
    () => localStorage.getItem('vibesprint_workspace_id') || null
  );
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const currentWorkspace = workspaces.find((w) => w.id === currentWorkspaceId) || workspaces[0] || null;

  const fetchWorkspaces = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/workspaces`);
      const list = response.data.workspaces || [];
      setWorkspaces(list);
      setUsage(response.data.usage || null);

      // Auto-pick first workspace if none selected or selected one no longer exists
      if (list.length > 0) {
        const stored = localStorage.getItem('vibesprint_workspace_id');
        const exists = stored && list.find((w) => w.id === stored);
        if (!exists) {
          setCurrentWorkspaceId(list[0].id);
          localStorage.setItem('vibesprint_workspace_id', list[0].id);
        } else {
          setCurrentWorkspaceId(stored);
        }
      }
    } catch (err) {
      console.error('Failed to load workspaces:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  const selectWorkspace = useCallback((id) => {
    setCurrentWorkspaceId(id);
    localStorage.setItem('vibesprint_workspace_id', id);
  }, []);

  const createWorkspace = useCallback(
    async ({ name, icon = '📦', color = '#8B5CF6' }) => {
      try {
        const response = await axios.post(`${API_URL}/api/workspaces`, {
          name,
          icon,
          color,
        });
        const newWs = response.data.workspace;
        await fetchWorkspaces();
        if (newWs?.id) {
          selectWorkspace(newWs.id);
        }
        toast.success(`Workspace "${name}" created`);
        return newWs;
      } catch (err) {
        const msg = err.response?.data?.detail || 'Failed to create workspace';
        toast.error(msg);
        throw err;
      }
    },
    [fetchWorkspaces, selectWorkspace]
  );

  const deleteWorkspace = useCallback(
    async (id) => {
      try {
        await axios.delete(`${API_URL}/api/workspaces/${id}`);
        toast.success('Workspace deleted');
        await fetchWorkspaces();
      } catch (err) {
        const msg = err.response?.data?.detail || 'Failed to delete workspace';
        toast.error(msg);
        throw err;
      }
    },
    [fetchWorkspaces]
  );

  const refreshUsage = fetchWorkspaces;

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces,
        currentWorkspaceId: currentWorkspace?.id || null,
        currentWorkspace,
        usage,
        loading,
        selectWorkspace,
        createWorkspace,
        deleteWorkspace,
        refreshUsage,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export default WorkspaceContext;
